import { companies, getJobsByCompany, type Company, type Job } from '../data/mockData';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type CompanyTestimonial = {
  id: string;
  person: string;
  title?: string;
  quote: string;
};

export type CompanyProfileData = {
  company?: Partial<
    Company & {
      websiteUrl?: string;
      about?: string;
      perks?: string[];
      highlights?: string[];
      gallery?: string[];
      cultureDeckUrl?: string;
    }
  >;
  openRoles: Job[];
  testimonials?: CompanyTestimonial[];
  highlights?: string[];
  nearbyEmployers?: Array<Pick<Company, 'id' | 'name' | 'industry' | 'location' | 'employees'>>;
  gallery?: string[];
  cultureDeckUrl?: string;
};

const slugify = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const findMockCompany = (slug: string): Company | undefined => {
  const numericId = Number(slug);
  if (!Number.isNaN(numericId)) {
    return companies.find((company) => company.id === numericId);
  }
  const safeSlug = slugify(slug);
  return companies.find((company) => slugify(company.name) === safeSlug);
};

const buildFallbackProfile = (slug: string): CompanyProfileData | undefined => {
  const company = findMockCompany(slug);
  if (!company) return undefined;
  const openRoles = getJobsByCompany(company.id).filter((job) => job.status === 'active');
  const nearbyEmployers = companies
    .filter((item) => item.location === company.location && item.id !== company.id)
    .slice(0, 3)
    .map((item) => ({ id: item.id, name: item.name, industry: item.industry, location: item.location, employees: item.employees }));

  return {
    company,
    openRoles,
    highlights: [
      'Dedicated coaching and phased onboarding for career comebacks.',
      'Flexible working practices with hybrid and remote options.',
      'Measurable inclusion targets reviewed quarterly with Lighthouse.',
    ],
    testimonials: [
      {
        id: 'testimonial-1',
        person: 'Aisha Patel',
        title: 'Programme Lead',
        quote: 'Lighthouse returners bring unmatched experience. Our coaching pods make re-entry seamless.',
      },
      {
        id: 'testimonial-2',
        person: 'Martin Lewis',
        title: 'Head of People',
        quote: 'The Lighthouse partnership means every hiring manager is trained on equitable hiring.',
      },
    ],
    gallery: [
      'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1529333166437-3eb64b6a0a06?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1529333166437-5ef199ad5d88?auto=format&fit=crop&w=900&q=80',
    ],
    cultureDeckUrl: 'https://lighthouse.jobs/culture.pdf',
    nearbyEmployers,
  };
};

export async function fetchCompanyProfile(slug: string): Promise<CompanyProfileData | undefined> {
  if (!slug) throw new Error('Company slug is required');

  if (!API_BASE_URL) {
    return buildFallbackProfile(slug);
  }

  const response = await fetch(`${API_BASE_URL}/v1/companies/${slug}`, {
    headers: { 'Content-Type': 'application/json' },
  });

  if (response.status === 404) {
    return undefined;
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Company fetch failed (${response.status}): ${text}`);
  }

  const payload = await response.json();
  const company = payload?.company ?? payload?.data ?? payload;
  const openRoles = (payload?.openRoles ?? payload?.jobs ?? []) as Job[];
  const testimonials = payload?.testimonials as CompanyTestimonial[] | undefined;
  const highlights = payload?.highlights ?? payload?.perks;
  const gallery = payload?.gallery as string[] | undefined;
  const cultureDeckUrl = payload?.cultureDeckUrl as string | undefined;
  const nearbyEmployers = payload?.nearbyEmployers as CompanyProfileData['nearbyEmployers'];

  return {
    company,
    openRoles,
    testimonials,
    highlights,
    nearbyEmployers,
    gallery,
    cultureDeckUrl,
  };
}
